import type { Theme } from "@mui/material/styles";
import type { AppSx } from "../types";

export interface SlideOverPanelComponentStyles {
  backdrop: AppSx;
  panel: (width: number, backgroundColor?: string) => AppSx;
  panelFullWidth: (backgroundColor?: string) => AppSx;
}

export function buildSlideOverPanelComponentStyles(theme: Theme): SlideOverPanelComponentStyles {
  const panelBase = (backgroundColor?: string): AppSx => ({
    position: "fixed",
    top: 0,
    bottom: 0,
    bgcolor: backgroundColor ?? "background.paper",
    boxShadow: theme.shadows[16],
    display: "flex",
    flexDirection: "column",
    outline: "none",
    overflow: "hidden",
  });

  return {
    backdrop: {
      bgcolor: "rgba(0, 0, 0, 0.35)",
      width: "100vw",
      height: "100vh",
    },
    panel: (width, backgroundColor) => ({
      ...panelBase(backgroundColor),
      right: 0,
      width: { xs: "100vw", sm: width },
      maxWidth: "100vw",
    }),
    panelFullWidth: (backgroundColor) => ({
      ...panelBase(backgroundColor),
      left: 0,
      right: 0,
      width: "100vw",
      maxWidth: "100vw",
    }),
  };
}

export interface GenericDialogComponentStyles {
  description: (hasError: boolean) => AppSx;
  form: AppSx;
}

export function buildGenericDialogComponentStyles(_theme: Theme): GenericDialogComponentStyles {
  return {
    description: (hasError) => ({ mb: hasError ? 1.5 : 0 }),
    form: { display: "flex", flexDirection: "column", m: 0, p: 0 },
  };
}

export interface DrillDownDialogComponentStyles {
  subtitle: AppSx;
  tableBox: AppSx;
  loadingCenter: AppSx;
}

export function buildDrillDownDialogComponentStyles(_theme: Theme): DrillDownDialogComponentStyles {
  return {
    subtitle: { display: "block", mb: 1 },
    tableBox: { overflow: "auto", maxHeight: 420 },
    loadingCenter: { display: "flex", justifyContent: "center", py: 4 },
  };
}

export interface OnboardingDialogComponentStyles {
  stepper: AppSx;
  welcomeStack: AppSx;
  welcomeIcon: AppSx;
  loadingBox: AppSx;
  dashboardCard: (selected: boolean) => AppSx;
  cardContent: AppSx;
  orCreate: AppSx;
}

export function buildOnboardingDialogComponentStyles(theme: Theme): OnboardingDialogComponentStyles {
  return {
    stepper: { mb: 3 },
    welcomeStack: { py: 1 },
    welcomeIcon: { fontSize: 48, color: "primary.main" },
    loadingBox: { display: "flex", justifyContent: "center", py: 3 },
    dashboardCard: (selected) => ({
      borderColor: selected ? "primary.main" : "divider",
      bgcolor: selected
        ? theme.palette.mode === "dark"
          ? "rgba(25, 118, 210, 0.12)"
          : "rgba(25, 118, 210, 0.06)"
        : "background.paper",
    }),
    cardContent: { py: 1.5 },
    orCreate: { pt: 1 },
  };
}
